import { useCallback, useEffect, useState } from "react";
import { Plus, KeyRound, RefreshCw, Ban, Copy, Terminal, Trash2, Server, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, BACKEND_URL, errMsg, fmtDate, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, PageHeader, Panel, StatusChip } from "@/components/common";
import DataTable from "@/components/DataTable";
import { cn } from "@/lib/utils";

const PLACEHOLDER = "__PASTE_API_KEY_HERE__";
const TYPES = [["vendors", "Vendors"], ["customers", "Customers"], ["po_lines", "PO lines"], ["stock", "Stock"], ["materials", "Materials"], ["sales", "Sales"]];
const DEFAULTS = { vendors: "Vendors*.xlsx", customers: "Customers*.xlsx", po_lines: "PO_Items*.csv", stock: "Stocks*.xlsx", materials: "Commodity Codes*.xlsx", sales: "Sales*.csv" };

function copy(text, what = "Copied") {
  navigator.clipboard.writeText(text).then(() => toast.success(what));
}

function CodeBlock({ code, testid, filename }) {
  return (
    <div className="relative overflow-hidden rounded-xl bg-[#1C1C1E]" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2 text-xs text-slate-400">
        <span className="font-mono">{filename}</span>
        <button onClick={() => copy(code, "Script copied")} className="flex items-center gap-1 rounded px-2 py-1 hover:bg-white/10 hover:text-white" data-testid={`${testid}-copy`}>
          <Copy className="h-3.5 w-3.5" /> Copy
        </button>
      </div>
      <pre className="scroll-thin max-h-[460px] overflow-auto p-4 text-[12px] leading-relaxed text-slate-200"><code>{code}</code></pre>
    </div>
  );
}

function SourceForm({ init, onSubmit, busy, submitLabel }) {
  const [f, setF] = useState(init);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Source name</label>
          <Input data-testid="source-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="UK File Server" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Daily time</label>
          <Input data-testid="source-time-input" type="time" value={f.schedule_time} onChange={(e) => set("schedule_time", e.target.value)} />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Folder on the file server</label>
        <Input data-testid="source-folder-input" className="font-mono text-xs" value={f.folder} onChange={(e) => set("folder", e.target.value)} />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">File pattern per type (leave blank to skip)</label>
        <div className="mt-1 grid grid-cols-2 gap-2">
          {TYPES.map(([t, l]) => (
            <div key={t} className="flex items-center gap-2">
              <span className="w-20 text-xs text-slate-600">{l}</span>
              <Input data-testid={`source-pattern-${t}`} className="h-8 font-mono text-xs" value={f.patterns[t] || ""} onChange={(e) => set("patterns", { ...f.patterns, [t]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Switch checked={f.archive} onCheckedChange={(v) => set("archive", v)} data-testid="source-archive-switch" /> Move uploaded files to a “processed” subfolder
      </label>
      <DialogFooter>
        <Button data-testid="source-submit-btn" disabled={!f.name.trim() || busy} onClick={() => onSubmit(f)}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitLabel}</Button>
      </DialogFooter>
    </div>
  );
}

function KeyReveal({ data, onClose }) {
  const [scripts, setScripts] = useState(null);
  useEffect(() => {
    if (!data) { setScripts(null); return; }
    Promise.all(["powershell", "python"].map((l) => api.get(`/scripts/sources/${data.id}/script`, { params: { lang: l, base_url: BACKEND_URL } }))).then(
      ([ps, py]) => setScripts({ ps: ps.data, py: py.data })
    );
  }, [data]);
  const inject = (s) => (data?.api_key ? s.replaceAll(PLACEHOLDER, data.api_key) : s);
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="key-reveal-dialog">
        <DialogHeader><DialogTitle>API key for “{data?.name}”</DialogTitle></DialogHeader>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Copy this key now — it will not be shown again.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-white px-3 py-2 font-mono text-sm text-slate-900" data-testid="revealed-api-key">{data?.api_key}</code>
            <Button size="sm" onClick={() => copy(data?.api_key || "", "API key copied")} data-testid="copy-api-key-btn"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
          </div>
        </div>
        {scripts && data && (
          <Tabs defaultValue="ps">
            <TabsList><TabsTrigger value="ps">PowerShell (key included)</TabsTrigger><TabsTrigger value="py">Python (key included)</TabsTrigger></TabsList>
            <TabsContent value="ps"><CodeBlock code={inject(scripts.ps.content)} filename={scripts.ps.filename} testid="reveal-ps-script" /></TabsContent>
            <TabsContent value="py"><CodeBlock code={inject(scripts.py.content)} filename={scripts.py.filename} testid="reveal-py-script" /></TabsContent>
          </Tabs>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="key-reveal-close">I have stored the key</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourceDetail({ s, isAdmin, onChanged, onKey }) {
  const [scripts, setScripts] = useState(null);
  useEffect(() => {
    setScripts(null);
    Promise.all(["powershell", "python"].map((l) => api.get(`/scripts/sources/${s.id}/script`, { params: { lang: l, base_url: BACKEND_URL } }))).then(
      ([ps, py]) => setScripts({ ps: ps.data, py: py.data })
    );
  }, [s]);
  const act = async (path, msg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const r = await api.post(`/scripts/sources/${s.id}/${path}`);
      toast.success(msg);
      onChanged();
      if (r.data.api_key) onKey(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete source “${s.name}”?`)) return;
    await api.delete(`/scripts/sources/${s.id}`);
    onChanged(true);
  };
  const sched = scripts?.ps.schedule;
  return (
    <Panel
      testid="source-detail"
      title={s.name}
      subtitle={`Key ${s.key_prefix}… · created ${fmtDate(s.created_at)} by ${s.created_by}`}
      action={isAdmin && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="rotate-key-btn" onClick={() => act("rotate", "Key rotated — old key no longer accepted", "Rotate the key? The current key stops working immediately.")}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Rotate key</Button>
          {s.status === "active" && <Button size="sm" variant="outline" data-testid="revoke-key-btn" onClick={() => act("revoke", "Key revoked", "Revoke this key? Scripts using it will receive 401.")}><Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke</Button>}
          <Button size="sm" variant="ghost" data-testid="delete-source-btn" onClick={del}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      )}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        {[["Status", <StatusChip status={s.status === "revoked" ? "revoked" : s.overdue ? "overdue" : "active"} label={s.status === "revoked" ? "Revoked" : s.overdue ? "Overdue" : "Active"} testid="source-detail-status" />],
          ["Last seen", <span data-testid="source-last-seen">{s.last_seen_at ? `${timeAgo(s.last_seen_at)}` : "never"}</span>],
          ["Runs", s.runs || 0], ["Daily at", s.schedule_time]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
            <div className="mt-0.5 text-sm font-medium text-slate-800">{v}</div>
          </div>
        ))}
      </div>
      <Tabs defaultValue="ps">
        <TabsList>
          <TabsTrigger value="ps" data-testid="tab-powershell">PowerShell</TabsTrigger>
          <TabsTrigger value="py" data-testid="tab-python">Python</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Run log</TabsTrigger>
        </TabsList>
        {scripts ? (
          <>
            <TabsContent value="ps"><CodeBlock code={scripts.ps.content} filename={scripts.ps.filename} testid="powershell-script" /></TabsContent>
            <TabsContent value="py"><CodeBlock code={scripts.py.content} filename={scripts.py.filename} testid="python-script" /></TabsContent>
            <TabsContent value="schedule">
              <div className="grid gap-4 lg:grid-cols-2" data-testid="schedule-instructions">
                {[["Windows Task Scheduler", sched.windows], ["Linux cron", sched.cron]].map(([t, steps]) => (
                  <div key={t} className="rounded-xl border border-slate-200 p-4">
                    <p className="mb-3 font-semibold text-slate-900">{t}</p>
                    <ol className="space-y-2.5">
                      {steps.map((st, i) => (
                        <li key={i} className="flex gap-3 text-sm text-slate-700">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1C1C1E] text-[11px] font-semibold text-white">{i + 1}</span>
                          <span className="min-w-0 break-words font-mono text-xs leading-relaxed">{st}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
                <p className="text-xs text-slate-500 lg:col-span-2">{sched.overdue_rule} Key is read from the EUDR_API_KEY environment variable, or paste it in place of {PLACEHOLDER}.</p>
              </div>
            </TabsContent>
          </>
        ) : <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        <TabsContent value="runs">
          <DataTable endpoint="/scripts/runs" params={{ source_id: s.id }} pageSize={15} testid="runs-table" columns={[
            { key: "at", label: "Time", render: (r) => fmtDate(r.at, true) },
            { key: "type", label: "Type" },
            { key: "filename", label: "File", className: "max-w-[200px] truncate" },
            { key: "status", label: "Result", render: (r) => <StatusChip status={r.status === "accepted" ? "accepted" : "failed"} label={r.status} testid={`run-status-${r.id}`} /> },
            { key: "reason", label: "Detail", className: "max-w-[260px] truncate", render: (r) => r.reason || (r.upload ? `${r.upload.status} · ${r.upload.inserted ?? 0} ins / ${r.upload.updated ?? 0} upd / ${r.upload.failed ?? 0} failed` : "—") },
            { key: "ip", label: "IP", render: (r) => <span className="font-mono text-xs">{r.ip}</span> },
          ]} />
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

export default function Scripts() {
  const { can } = useAuth();
  const [sources, setSources] = useState([]);
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const load = useCallback(async (reset) => {
    const r = await api.get("/scripts/sources");
    setSources(r.data);
    setSel((cur) => (reset || !cur ? r.data[0]?.id || null : cur));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (f) => {
    setBusy(true);
    try {
      const r = await api.post("/scripts/sources", f);
      setCreating(false);
      setRevealed(r.data);
      await load();
      setSel(r.data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const current = sources.find((s) => s.id === sel);

  return (
    <div data-testid="scripts-page">
      <PageHeader title="Scripts & API keys" subtitle="Automate the daily SAP extract push from file servers. Each source gets its own API key, PowerShell and Python script, and a run log.">
        {can.admin && <Button data-testid="create-source-btn" onClick={() => setCreating(true)}><Plus className="mr-1.5 h-4 w-4" /> New source</Button>}
      </PageHeader>
      <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
        <div className="space-y-2.5" data-testid="sources-list">
          {sources.length === 0 && <Empty icon={Server} title="No script sources" text="Create a source for each file server that exports SAP files." />}
          {sources.map((s) => (
            <button key={s.id} onClick={() => setSel(s.id)} data-testid={`source-card-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
              className={cn("w-full rounded-xl border bg-white p-4 text-left transition-[box-shadow,border-color] duration-150 hover:shadow-sm", sel === s.id ? "border-[#D52B3B] ring-1 ring-[#D52B3B]/20" : "border-slate-200")}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-medium text-slate-900"><Terminal className="h-4 w-4 text-slate-400" />{s.name}</span>
                <StatusChip status={s.status === "revoked" ? "revoked" : s.overdue ? "overdue" : "active"} label={s.status === "revoked" ? "Revoked" : s.overdue ? "Overdue" : "Active"} testid={`source-status-${s.id}`} />
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><KeyRound className="h-3 w-3" /> <span className="font-mono">{s.key_prefix}…</span> · last seen {timeAgo(s.last_seen_at)}</p>
            </button>
          ))}
        </div>
        <div>{current ? <SourceDetail s={current} isAdmin={can.admin} onChanged={load} onKey={setRevealed} /> : null}</div>
      </div>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-xl" data-testid="create-source-dialog">
          <DialogHeader><DialogTitle>New script source</DialogTitle></DialogHeader>
          <SourceForm init={{ name: "", folder: "\\\\fileserver\\SAP\\exports", schedule_time: "06:00", archive: true, patterns: DEFAULTS }} onSubmit={create} busy={busy} submitLabel="Create & generate key" />
        </DialogContent>
      </Dialog>
      <KeyReveal data={revealed} onClose={() => setRevealed(null)} />
      {false && <Check />}
    </div>
  );
}
